import { motion } from "framer-motion";
import { Gavel, ShieldCheck, Zap, BarChart3, Users, Trophy } from "lucide-react";

const FEATURES_VISUAL =
  "https://images.unsplash.com/photo-1718653070244-e9356b63605b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxyYXAlMjBiYXR0bGV8ZW58MHx8fHwxNzkxNzU4OTk1fDA&ixlib=rb-4.1.0&q=85";

const Card = ({ className = "", children, testid, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay }}
    data-testid={testid}
    className={`relative overflow-hidden border border-white/10 bg-[#0F172A] p-8 ${className}`}
  >
    {children}
  </motion.div>
);

export default function Features() {
  return (
    <section
      id="caracteristicas"
      data-testid="features-section"
      className="relative scroll-mt-24 border-y border-white/10 bg-[#0B1120]/40 py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-14 flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-[#FF5500]" />
            <span className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF5500]">
              Características
            </span>
          </div>
          <h2 className="max-w-2xl font-heading text-3xl font-black leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Lo que hace distinta a La Pinza
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
          {/* Votación de jueces — large */}
          <Card testid="feature-votacion" className="md:col-span-7" delay={0}>
            <div
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: `url(${FEATURES_VISUAL})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#0F172A]/40" />
            <div className="relative flex h-full min-h-[240px] flex-col justify-end gap-4">
              <Gavel className="h-8 w-8 text-[#FF5500]" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-bold tracking-tight text-white">
                Votación de jueces en vivo
              </h3>
              <p className="max-w-md font-body text-sm leading-relaxed text-[#94A3B8]">
                Cada jurado puntúa en tiempo real desde su dispositivo. Los votos
                se registran al instante y quedan atados a cada enfrentamiento del
                bracket.
              </p>
            </div>
          </Card>

          {/* Transparencia */}
          <Card testid="feature-transparencia" className="md:col-span-5" delay={0.08}>
            <div className="flex h-full flex-col gap-4">
              <ShieldCheck className="h-8 w-8 text-[#FF5500]" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-bold tracking-tight text-white">
                Transparencia total
              </h3>
              <p className="font-body text-sm leading-relaxed text-[#94A3B8]">
                El público ve exactamente quién votó qué. Sin cajas negras: cada
                puntuación es auditable y verificable.
              </p>
            </div>
          </Card>

          {/* Dinamismo */}
          <Card testid="feature-dinamismo" className="md:col-span-5" delay={0.16}>
            <div className="flex h-full flex-col gap-4">
              <Zap className="h-8 w-8 text-[#FF5500]" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-bold tracking-tight text-white">
                Dinamismo en vivo
              </h3>
              <p className="font-body text-sm leading-relaxed text-[#94A3B8]">
                Brackets que se actualizan solos, animaciones de avance y
                resultados que corren al ritmo de la batalla.
              </p>
            </div>
          </Card>

          {/* Mini stats row */}
          <Card testid="feature-rankings" className="md:col-span-7" delay={0.24}>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {[
                { icon: BarChart3, t: "Rankings", d: "Tabla de posiciones automática" },
                { icon: Users, t: "Multi-MC", d: "Historial de cada participante" },
                { icon: Trophy, t: "Campeones", d: "Palmarés por evento" },
              ].map((x) => (
                <div key={x.t} className="flex flex-col gap-2.5">
                  <x.icon className="h-6 w-6 text-[#FF5500]" strokeWidth={1.5} />
                  <h4 className="font-ui text-base font-bold text-white">{x.t}</h4>
                  <p className="font-body text-xs leading-relaxed text-[#94A3B8]">
                    {x.d}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
